import { DemoChip, InfoTipPopover } from '@frontend/analytics-shared';
import { Chart } from '@frontend/charts';
import { i18next, Trans, useTranslation } from '@frontend/i18n';
import { useScopedAppFlagStore } from '@frontend/scope';
import { useSettingsNavigate } from '@frontend/settings-routing';
import { formatters } from '@frontend/shared';
import { theme } from '@frontend/theme';
import { Text } from '@frontend/design-system';
import { featureFlags } from '../../../../feature-flags';
import { CompareLocationsButton } from '../../../compare-locations-button';
import { NoShowRateBestPractices } from '../components/no-show-rate-best-practices';
import { useFetchARROIOOverview } from '../hooks/use-fetch-ar-roi-overview';
import { ARChartsBase } from './ar-charts-base';

const colors = {
  noRemindersPractice: theme.colors.critical30,
  yourPractice: theme.colors.secondary.seaweed40,
};

const labels = {
  noRemindersPractice: i18next.t('Practices not using appointment reminders', { ns: 'analytics' }),
  yourPractice: i18next.t('Your practice', { ns: 'analytics' }),
};

const trackingIdBase = 'no-show-rate';

interface Props extends ReturnType<typeof useFetchARROIOOverview> {
  onShowMoreClick?: () => void;
  showCompareMulti?: boolean;
}

const ShowRateMessage = ({ average }: { average: number }) => {
  const { t } = useTranslation('analytics');

  let content = <></>;

  if (average < 0) {
    content = (
      <Trans t={t} values={{ average: formatters.appendPercent(average) }}>
        Your practice has an average of <strong>{{ average }}</strong> higher no-show rate compared to practices not
        using appointment reminders.
      </Trans>
    );
  } else {
    content = (
      <Trans t={t} values={{ average: formatters.appendPercent(Math.abs(average)) }}>
        Your practice has an average of <strong>{{ average }}</strong> lower no-show rate compared to practices not
        using appointment reminders.
      </Trans>
    );
  }

  return <Text>{content}</Text>;
};

export const ARRoiNoShowRateChart = ({ aggregated, isLoading, onShowMoreClick, showCompareMulti }: Props) => {
  const { t } = useTranslation('analytics');
  const { getFeatureFlagValue } = useScopedAppFlagStore();
  const isDemoAccount = getFeatureFlagValue(featureFlags.appointmentRemindersRoiDemoData);
  const { navigate } = useSettingsNavigate();

  if (aggregated?.noShowRate?.enableNoShowRateBestPractices) {
    return (
      <Chart isLoading={isLoading}>
        <Chart.Header title={t('Best Practices to lower No-Show Rates')} leftElement={isDemoAccount && <DemoChip />} />
        <ARChartsBase
          onShowMoreClick={() => {
            navigate({ to: '/messaging/auto-messages' });
          }}
          showMoreLabel={t('Optimize Appointment Reminder Usage')}
          trackingIdBase={trackingIdBase}
        >
          <NoShowRateBestPractices />
        </ARChartsBase>
      </Chart>
    );
  }

  return (
    <Chart
      colors={colors}
      isLoading={isLoading}
      labels={labels}
      noBorder={!onShowMoreClick}
      onClick={onShowMoreClick}
      showOverflow={!onShowMoreClick}
      trackingId={`${trackingIdBase}-chart`}
    >
      {onShowMoreClick && (
        <Chart.Header
          title={t('No-Show Rate')}
          infoTip={
            <InfoTipPopover>
              {t(
                'The No-Show Rate is the percentage of scheduled appointments that patients fail to attend without prior notice or cancellation.'
              )}
            </InfoTipPopover>
          }
          bottomElement={
            showCompareMulti && (
              <CompareLocationsButton onClick={onShowMoreClick} trackingId={`${trackingIdBase}-compare-locations`} />
            )
          }
          leftElement={isDemoAccount && <DemoChip />}
          removeHeaderBottomMargin={!showCompareMulti}
        />
      )}
      <ShowRateMessage average={aggregated.noShowRate.difference} />
      <ARChartsBase onShowMoreClick={onShowMoreClick} trackingIdBase={trackingIdBase}>
        <Chart.AreaChart
          appearance={{
            customYAxisTickFormat: formatters.appendPercent,
            height: 200,
            margin: { top: 30 },
            showXAxis: true,
            showYAxis: true,
            xAxisTickWidth: 100,
          }}
          data={{ groups: aggregated.noShowRate.groups }}
          formatValue={formatters.appendPercent}
        />
        <Chart.Legends style={{ marginTop: theme.spacing(3) }} truncateLabels />
      </ARChartsBase>
    </Chart>
  );
};
